import { Link, useSearchParams } from "react-router-dom";
import { useSEO, breadcrumb } from "../lib/seo";
import { ASSETS, SITE } from "../lib/site";
import PageHero from "../components/PageHero";
import { getArticles } from "../data/articles";
import { trackEvent } from "../lib/analytics";
import { ArrowRight, Calendar, Clock } from "lucide-react";

export default function Articles() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const articles = getArticles();
  const normalizedQuery = query.trim().toLowerCase();
  const filteredArticles = normalizedQuery
    ? articles.filter((article) =>
        [
          article.title,
          article.excerpt,
          article.category,
          article.keywords,
          article.body,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(normalizedQuery)
      )
    : articles;

  useSEO({
    title: "Artikel Sloto69 | Panduan, Keamanan, dan Informasi Resmi",
    description: "Artikel edukatif Sloto69 dalam bahasa Indonesia: panduan login, daftar, link alternatif, keamanan akun, dan informasi resmi.",
    keywords: "artikel sloto69, panduan sloto69, informasi sloto69",
    canonical: `${SITE.baseUrl}/artikel`,
    image: `${SITE.baseUrl}${ASSETS.ogImage}`,
    jsonLd: breadcrumb([
      { name: "Beranda", url: `${SITE.baseUrl}/` },
      { name: "Artikel", url: `${SITE.baseUrl}/artikel` },
    ]),
  });

  return (
    <>
      <PageHero
        eyebrow="Artikel"
        title="Bacaan Edukatif untuk Pengguna Sloto69 di Indonesia"
        description="Kumpulan artikel pilihan untuk membantu Anda memahami panduan, keamanan, hingga akses informasi resmi Sloto69."
        breadcrumb={[{ label: "Artikel" }]}
      />

      <section className="section-pad" data-testid="article-list">
        <div className="shell mb-8">
          <label className="grid gap-2 max-w-2xl">
            <span className="text-sm font-semibold text-slate-200">Cari Artikel</span>
            <input
              type="search"
              value={query}
              onChange={(event) => {
                const next = event.target.value;
                setSearchParams(next ? { q: next } : {});
                if (next.trim().length >= 3) {
                  trackEvent("search_article", {
                    event_category: "search",
                    search_term: next.trim(),
                    location: "article_list",
                  });
                }
              }}
              data-testid="article-search-input"
              placeholder="Cari panduan login, daftar, QRIS, link alternatif..."
              className="h-12 rounded-lg border border-white/10 bg-[#0F172A]/70 px-4 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#10B981]/60 focus:ring-1 focus:ring-[#10B981]/30"
            />
          </label>
          {normalizedQuery && (
            <p className="mt-3 text-sm text-slate-400">
              Menampilkan {filteredArticles.length} artikel untuk "{query}".
            </p>
          )}
        </div>
        <div className="shell grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredArticles.map((a, i) => (
            <Link
              key={a.slug}
              to={`/artikel/${a.slug}`}
              data-testid={`article-list-card-${i}`}
              onClick={() =>
                trackEvent("read_article", {
                  event_category: "content",
                  event_label: a.title,
                  article_slug: a.slug,
                  location: "article_list",
                })
              }
              className="card-premium p-6 flex flex-col cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#10B981]"
              aria-label={`Baca artikel ${a.title}`}
            >
              {a.imageUrl && (
                <img
                  src={a.imageUrl}
                  alt={a.title}
                  className="-mx-1 mb-5 aspect-[16/10] w-[calc(100%+0.5rem)] rounded-md object-cover"
                  loading="lazy"
                />
              )}
              <span className="text-xs text-[#D4AF37] tracking-widest uppercase">{a.category}</span>
              <h2 className="mt-3 font-display text-xl font-semibold leading-snug">{a.title}</h2>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed flex-1">{a.excerpt}</p>
              <div className="mt-5 flex items-center justify-between text-xs text-slate-500">
                <span className="inline-flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" /> {a.publishedAt}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" /> {a.readTime}
                </span>
              </div>
              <span
                data-testid={`article-list-read-${i}`}
                className="mt-5 inline-flex items-center gap-1 text-sm text-[#10B981] transition-all"
              >
                Baca Selengkapnya <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          ))}
        </div>
        {filteredArticles.length === 0 && (
          <div className="shell mt-8">
            <div className="card-premium p-6 text-center">
              <h2 className="font-display text-2xl">Artikel tidak ditemukan</h2>
              <p className="mt-2 text-sm text-slate-400">
                Coba gunakan kata kunci lain seperti login, daftar, QRIS, atau keamanan.
              </p>
            </div>
          </div>
        )}
      </section>
    </>
  );
}
